import type { HTMLProps } from "react";
import { useState } from "react";
import styled from "styled-components";
// @ts-ignore - colord has module resolution issues in strict mode
import { colord } from "colord";
import { isPromise } from "@utils/utils";

type ButtonType =
	| "link"
	| "neutral"
	| "brand"
	// | "inverse"
	// | "destructive"
	| "success"
	| "secondary"
	| "outline"
	| "outline-brand"
	| "outline-success"
	| "outline-destructive";

type ButtonSize = "xs" | "s" | "m" | "l";

interface Props extends Omit<HTMLProps<HTMLButtonElement>, "type" | "size" | "onClick"> {
	type: ButtonType;
	size?: ButtonSize;
	title: string;
	loadingTitle?: string;
	onClick: () => any;
	icon?: (disabled?: boolean) => React.ReactElement<any, any>;
	ctnClassName?: string;
}

export const Button: React.FC<Props> = ({
	type,
	size,
	className,
	ctnClassName,
	onClick,
	children,
	title,
	loadingTitle,
	disabled,
	icon,
	style,
	...props
}) => {
	const [loading, setLoading] = useState(false);

	async function handleClick() {
		const res = onClick();

		if (isPromise(res)) {
			setLoading(true);
			await res;
		}
	}

	return (
		<Styles style={style} className={ctnClassName}>
			<button
				className={`${type}-btn ${size} ${className ?? ""}`}
				{...props}
				onClick={(e) => {
					e.stopPropagation();
					handleClick();
				}}
				type="button"
				disabled={disabled}
			>
				{icon && <span className="btn-icon">{icon(loading || disabled)}</span>}
				{loading && loadingTitle ? loadingTitle : title}
			</button>
		</Styles>
	);
};

const Styles = styled.div`
	/* width: 100%; */
	button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		padding: 9px;
		cursor: pointer;
		border-style: none;
		border-radius: ${(props) => props.theme.buttons.radius}px;
		font-size: 0.9rem;
		font-weight: 500;
		letter-spacing: -0.5px;
		transition: transform 0.125s;

		&:active {
			transform: scale(0.98);
		}

		&.l {
			font-size: 1.1rem;
			padding: 12px;
		}

		&.s {
			font-size: 0.8rem;
			padding: 4px 6px;
			border-radius: ${(props) => props.theme.buttons.radius / 2}px;
		}

		&.xs {
			font-size: 0.75rem;
			padding: 2px 5px;
			border-radius: ${(props) => props.theme.buttons.radius / 2}px;
		}

		&[disabled] {
			cursor: not-allowed;
			opacity: 0.85;
		}

		&.link-btn {
			text-decoration: underline;
			color: ${(props) => props.theme.colors.brand};
			background-color: transparent;
		}

		&.brand-btn {
			background-color: ${(props) => props.theme.colors.brand};
			color: white;
			border: 1px solid ${(props) => colord(props.theme.colors.brand).darken(0.05).toHex()};

			&:hover {
				background-color: ${(props) => colord(props.theme.colors.brand).darken(0.1).toHex()};
			}

			&[disabled] {
				background-color: ${(props) => colord(props.theme.colors.brand).desaturate(0.5).lighten(0.2).toHex()};
			}
		}

		&.success-btn {
			background-color: ${(props) => props.theme.colors.success};
			color: white;
			border: 1px solid ${(props) => colord(props.theme.colors.success).darken(0.05).toHex()};

			&:hover {
				background-color: ${(props) => colord(props.theme.colors.success).darken(0.1).toHex()};
			}

			&[disabled] {
				background-color: ${(props) => colord(props.theme.colors.success).desaturate(0.5).lighten(0.2).toHex()};

				border-color: ${(props) => colord(props.theme.colors.success).desaturate(0.5).lighten(0.1).toHex()};
			}
		}

		&.secondary-btn {
			background-color: ${(props) => props.theme.colors.secondary};
			color: white;

			&:hover {
				background-color: ${(props) => colord(props.theme.colors.secondary).darken(0.1).toHex()};
			}
		}

		&.outline-btn {
			border: 1px solid ${(props) => props.theme.colors.dividerLight};
			color: ${(props) => props.theme.colors.text};
			background: transparent;

			&:hover {
				border-color: ${(props) => props.theme.colors.divider};
			}
		}

		&.outline-brand-btn {
			border: 1px solid ${(props) => props.theme.colors.dividerLight};
			color: ${(props) => props.theme.colors.brand};
			background: transparent;

			&:hover {
				border-color: ${(props) => props.theme.colors.divider};
			}

			&[disabled] {
				color: ${(props) => colord(props.theme.colors.divider).lighten(0.02).toHex()};
			}
		}

		&.outline-success-btn {
			border: 1px solid ${(props) => props.theme.colors.dividerLight};
			color: ${(props) => props.theme.colors.success};
			background: transparent;

			&:hover {
				border-color: ${(props) => props.theme.colors.divider};
			}

			&[disabled] {
				color: ${(props) => colord(props.theme.colors.divider).lighten(0.02).toHex()};
			}
		}

		&.outline-destructive-btn {
			border: 1px solid ${(props) => props.theme.colors.dividerLight};
			color: ${(props) => props.theme.colors.destructive};
			background: transparent;
		}

		.btn-icon {
			margin-right: 5px;
		}
	}
`;
